'use client';

/**
 * USER DOCS: changes to this component should be accompanied by a
 * corresponding update to the user guide:
 * next_portal/user-docs/content/docs/getting-started/my-corner.mdx
 * (published at user-docs.totalaccess.co.za/getting-started/my-corner#my-devices)
 */

import '@/app/professional-modern.css';
import { useCallback, useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import { createPortal } from 'react-dom';
import {
  Activity,
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Copy,
  Download,
  ExternalLink,
  Globe,
  Loader2,
  Monitor,
  MonitorSmartphone,
  RefreshCw,
  Shield,
  Smartphone,
  Trash2,
  X,
} from 'lucide-react';
import { QRCodeCanvas } from 'qrcode.react';
import { apiService } from '@/lib/apiService';
import type { AppInstallCta } from '@/lib/appInstallCta';
import { useAuth } from '@/hooks/useAuth';
import { notify } from '@/lib/notifications';
import { STATIC_FILE_SERVER_DOWNLOADS_URL, STATIC_FILE_SERVER_BASE_URL } from '@/lib/staticFileServerUrl';
import { persistRememberedUsers, type RememberedUser } from '@/lib/rememberedUsers';
import UserDocsLink from '@/components/shared/UserDocsLink';

const IS_PRODUCTION_ENV = process.env.NEXT_PUBLIC_ENVIRONMENT === 'production';
const HUAWEI_APK_DOWNLOAD_URL = `${STATIC_FILE_SERVER_DOWNLOADS_URL}/${IS_PRODUCTION_ENV ? 'TotalAccess_Huawei_latest.apk' : 'TotalAccess_Huawei_Dev_latest.apk'}`;

type DeviceSessionKind = 'portal' | 'installed_pwa' | 'native_app' | 'desktop';

interface TrustedBrowser {
  browser_id: string;
  app_id: string;
  device_name: string;
  browser_label: string;
  trusted_at: string | null;
  trusted_until: string | null;
  ip_address: string | null;
  is_current: boolean;
}

interface DeviceSession {
  session_id: string;
  platform: string;
  raw_platform: string | null;
  platform_label: string;
  session_kind: DeviceSessionKind;
  device_name: string;
  last_active: string | null;
  has_push_token: boolean;
  is_current: boolean;
  created_at: string | null;
  app_id: string | null;
  mfa_trust_disabled?: boolean;
}

interface DeviceSessionsResponse {
  success: boolean;
  sessions?: DeviceSession[];
  trusted_browsers?: TrustedBrowser[];
  error?: string;
}

interface MyDevicesModalProps {
  isOpen?: boolean;
  onClose?: () => void;
  onOpenPlayStore: () => void;
  onOpenPwaInstall: () => void;
  isStandalonePwaInstalled: boolean;
  userAppId?: string | null;
  loginHint?: string | null;
  installContext: AppInstallCta;
  variant?: 'modal' | 'page';
  backHref?: string;
  onOpenAppGallery?: () => void;
}

const formatSessionDate = (value: string | null) => {
  if (!value) return '—';

  try {
    return new Date(value).toLocaleString('en-ZA', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return value;
  }
};

const sessionKindMeta: Record<DeviceSessionKind, {
  label: string;
  icon: React.ElementType;
  badgeClass: string;
}> = {
  portal: {
    label: 'Browser',
    icon: Globe,
    badgeClass: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  },
  installed_pwa: {
    label: 'App on this device',
    icon: MonitorSmartphone,
    badgeClass: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300',
  },
  native_app: {
    label: 'Mobile app',
    icon: Smartphone,
    badgeClass: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  },
  desktop: {
    label: 'Desktop app',
    icon: Monitor,
    badgeClass: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
  },
};

export default function MyDevicesModal({
  isOpen = true,
  onClose,
  onOpenPlayStore,
  onOpenPwaInstall,
  isStandalonePwaInstalled,
  userAppId,
  loginHint,
  installContext,
  variant = 'modal',
  backHref = '/client-zone/dashboard',
  onOpenAppGallery,
}: MyDevicesModalProps) {
  const { logout } = useAuth();
  const isPage = variant === 'page';
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(() => (
    typeof document !== 'undefined' ? document.body : null
  ));
  const [sessions, setSessions] = useState<DeviceSession[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [revokingSessionIds, setRevokingSessionIds] = useState<string[]>([]);
  const [revokingTrustedBrowserIds, setRevokingTrustedBrowserIds] = useState<string[]>([]);
  const [revokingNativeTrustAppIds, setRevokingNativeTrustAppIds] = useState<string[]>([]);
  const [bulkRevoking, setBulkRevoking] = useState(false);
  const [bulkRemovingTrust, setBulkRemovingTrust] = useState(false);
  const [bulkSigningOutAll, setBulkSigningOutAll] = useState(false);
  const [copiedSetupLink, setCopiedSetupLink] = useState(false);
  const [mobileSetupUrl, setMobileSetupUrl] = useState('');
  const [generatingSecureLink, setGeneratingSecureLink] = useState(false);
  const [secureLinkError, setSecureLinkError] = useState<string | null>(null);
  const [trustedBrowsers, setTrustedBrowsers] = useState<TrustedBrowser[]>([]);

  const isCurrentBrowserMobile = useMemo(() => {
    if (typeof window === 'undefined') {
      return false;
    }

    const nav = window.navigator as Navigator & {
      userAgentData?: {
        platform?: string;
      };
    };

    const userAgent = (nav.userAgent || '').toLowerCase();
    const platform = (nav.userAgentData?.platform || nav.platform || '').toLowerCase();
    const hasTouchMacProfile = platform === 'macintel' && nav.maxTouchPoints > 1;
    const isIOS = /iphone|ipad|ipod/.test(userAgent)
      || platform.includes('iphone')
      || platform.includes('ipad')
      || hasTouchMacProfile;

    return isIOS || /android|mobile|tablet/.test(userAgent) || platform.includes('android');
  }, []);

  const fallbackMobileSetupUrl = useMemo(() => {
    if (typeof window === 'undefined') {
      return '';
    }

    const redirectTarget = '/client-zone/my-devices?source=my-devices&entry=qr-handoff';
    const url = new URL('/auth/mobile-handoff', window.location.origin);
    url.searchParams.set('redirect', redirectTarget);
    if (loginHint) {
      url.searchParams.set('login_hint', loginHint);
    }
    return url.toString();
  }, [loginHint]);

  const showAndroidInstallCard = installContext.mode === 'android-store';
  const showAppGalleryInstallCard = installContext.mode === 'appgallery';
  const showInstalledWebAppCard = installContext.mode === 'authenticated-install';
  const showContinueOnPhoneCard = installContext.mode === 'mobile-device-required';
  const showRedirectToAppHostCard = installContext.mode === 'redirect-to-app-host';
  const showAndroidFallbackAction = (showAndroidInstallCard || showAppGalleryInstallCard) && !isStandalonePwaInstalled;
  const installedWebAppCardTitle = isStandalonePwaInstalled ? 'Open TotalAccess on this device' : 'Add TotalAccess to this device';
  const installedWebAppCardDescription = isStandalonePwaInstalled
    ? 'Open TotalAccess from this device for faster, easier access.'
    : 'Add TotalAccess to this device so it is easy to open whenever you need it.';
  const installedWebAppCardAction = isStandalonePwaInstalled ? 'Open setup help' : 'Add to this device';
  const installedWebAppFlowLabel = isStandalonePwaInstalled
    ? 'Open TotalAccess on this device because Google Play is not available here.'
    : 'Add TotalAccess to this device because Google Play is not available here.';

  const loadSessions = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiService.get<DeviceSessionsResponse>('/api/v1/sessions/active?view=devices');

      if (!response.success) {
        throw new Error(response.error || 'Unable to load your devices');
      }

      setSessions(response.sessions || []);
      setTrustedBrowsers(response.trusted_browsers || []);
    } catch (loadError) {
      console.error('[MyDevicesModal] Failed to load sessions:', loadError);
      setError(loadError instanceof Error ? loadError.message : 'Unable to load your devices right now.');
      setSessions([]);
      setTrustedBrowsers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    void loadSessions();
  }, [isOpen, loadSessions]);

  useEffect(() => {
    if (!isOpen || isPage || !onClose) {
      return;
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleEscape);

    return () => {
      window.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, isPage, onClose]);

  const mobileSessions = useMemo(
    () => sessions.filter((session) => session.session_kind === 'native_app' || session.session_kind === 'installed_pwa'),
    [sessions]
  );

  const portalSessions = useMemo(
    () => sessions.filter((session) => session.session_kind === 'portal'),
    [sessions]
  );

  const desktopSessions = useMemo(
    () => sessions.filter((session) => session.session_kind === 'desktop'),
    [sessions]
  );

  const hasMobileConflict = mobileSessions.length > 1;
  const currentSession = useMemo(
    () => sessions.find((session) => session.is_current) || null,
    [sessions]
  );
  const isSignedInMobileBrowser = useMemo(() => {
    if (currentSession) {
      return currentSession.session_kind === 'portal' && currentSession.platform === 'mobile';
    }

    return isCurrentBrowserMobile;
  }, [currentSession, isCurrentBrowserMobile]);
  const showQrHandoffCard = showContinueOnPhoneCard && !isSignedInMobileBrowser && !userAppId;
  const showContinueOnThisDeviceCard = showContinueOnPhoneCard && isSignedInMobileBrowser;
  const installSectionTitle = showAndroidInstallCard
    ? 'Recommended setup for this device'
    : showAppGalleryInstallCard
      ? 'Recommended setup for this Huawei device'
      : showInstalledWebAppCard
        ? `Recommended setup for ${installContext.platformLabel}`
        : showRedirectToAppHostCard
          ? 'Continue to install on the app domain'
          : showContinueOnThisDeviceCard
            ? 'Continue on this device'
            : 'Set up TotalAccess';
  const installSectionDescription = showContinueOnThisDeviceCard
    ? 'You are already signed in on this device, so you can continue setup here without scanning a QR code.'
    : showQrHandoffCard
      ? 'Open TotalAccess on your phone to finish setup there.'
      : showRedirectToAppHostCard
        ? 'The PWA must be installed from the TotalAccess app domain so it opens in standalone mode. You will be redirected there automatically.'
        : installContext.description;

  useEffect(() => {
    if (!isOpen || !showQrHandoffCard || !fallbackMobileSetupUrl) {
      return;
    }

    let isCancelled = false;

    const createSecureLink = async () => {
      setGeneratingSecureLink(true);
      setSecureLinkError(null);
      setMobileSetupUrl(fallbackMobileSetupUrl);

      try {
        const response = await apiService.post<{
          success: boolean;
          token?: string;
          error?: string;
        }>('/api/v1/auth/mobile-handoff/create', {
          loginHint,
        });

        if (!response.success || !response.token) {
          throw new Error(response.error || 'Unable to prepare phone sign-in');
        }

        if (isCancelled) {
          return;
        }

        const secureUrl = new URL(fallbackMobileSetupUrl);
        secureUrl.searchParams.set('handoff_token', response.token);
        setMobileSetupUrl(secureUrl.toString());
      } catch (linkError) {
        console.error('[MyDevicesModal] Failed to create secure handoff link:', linkError);
        if (!isCancelled) {
          setSecureLinkError('We could not prepare automatic sign-in, so this link will open the sign-in page instead.');
          setMobileSetupUrl(fallbackMobileSetupUrl);
        }
      } finally {
        if (!isCancelled) {
          setGeneratingSecureLink(false);
        }
      }
    };

    void createSecureLink();

    return () => {
      isCancelled = true;
    };
  }, [fallbackMobileSetupUrl, isOpen, loginHint, showQrHandoffCard]);

  const revokeSessions = useCallback(async (sessionIds: string[]) => {
    if (sessionIds.length === 0) {
      return;
    }

    const response = await apiService.post<{ success: boolean; error?: string }>('/api/v1/sessions/active', {
      action: 'revoke',
      session_ids: sessionIds,
    });

    if (!response.success) {
      throw new Error(response.error || 'Unable to revoke session');
    }
  }, []);

  const clearRememberedUser = useCallback((userEmail: string) => {
    if (!userEmail || typeof window === 'undefined') {
      return;
    }

    try {
      const saved = localStorage.getItem('remembered_users');
      if (saved) {
        const users = JSON.parse(saved);
        const filteredUsers = users.filter((u: RememberedUser) => u.email !== userEmail);
        persistRememberedUsers(filteredUsers);

        // Also clear legacy format if it matches
        const legacySaved = localStorage.getItem('remembered_user');
        if (legacySaved) {
          const legacyData = JSON.parse(legacySaved);
          if (legacyData.email === userEmail) {
            localStorage.removeItem('remembered_user');
          }
        }

        // Clear PIN failure count for this user
        const pinFailures = localStorage.getItem('pin_login_failures');
        if (pinFailures) {
          const failures = JSON.parse(pinFailures);
          delete failures[userEmail];
          localStorage.setItem('pin_login_failures', JSON.stringify(failures));
        }

        console.log('[MyDevicesModal] Cleared remembered user data for:', userEmail);
      }
    } catch (error) {
      console.error('[MyDevicesModal] Failed to clear remembered user data:', error);
    }
  }, []);

  const handleRevokeSession = useCallback(async (sessionId: string) => {
    const sessionToRevoke = sessions.find((s) => s.session_id === sessionId);
    const isRevokingCurrentSession = sessionToRevoke?.is_current;

    // If revoking the current session, show confirmation and use logout
    if (isRevokingCurrentSession) {
      const confirmed = await notify.confirm({
        title: 'Sign Out',
        message: 'Are you sure you want to sign out? You will need to sign in again to access your account.',
        confirmText: 'Sign Out',
        cancelText: 'Cancel',
        variant: 'danger',
      });

      if (!confirmed) {
        return;
      }

      // Clear remembered user data before logging out
      if (loginHint) {
        clearRememberedUser(loginHint);
      }

      try {
        await logout();
        // authService handles the redirect to the signed-out login page
      } catch (error) {
        console.error('[MyDevicesModal] Logout error:', error);
        setError('Unable to sign out. Please try again.');
      }
      return;
    }

    // For non-current sessions, use the revoke API
    setRevokingSessionIds((current) => [...current, sessionId]);
    setError(null);

    try {
      await revokeSessions([sessionId]);
      await loadSessions();
    } catch (revokeError) {
      console.error('[MyDevicesModal] Failed to revoke session:', revokeError);
      setError(revokeError instanceof Error ? revokeError.message : 'Unable to sign out that device.');
    } finally {
      setRevokingSessionIds((current) => current.filter((id) => id !== sessionId));
    }
  }, [loadSessions, revokeSessions, sessions, loginHint, clearRememberedUser, logout]);

  const handleSignOutOtherMobileDevices = useCallback(async () => {
    const targetSessionIds = mobileSessions.filter((session) => !session.is_current).map((session) => session.session_id);

    if (targetSessionIds.length === 0) {
      return;
    }

    setBulkRevoking(true);
    setError(null);

    try {
      await revokeSessions(targetSessionIds);
      await loadSessions();
    } catch (revokeError) {
      console.error('[MyDevicesModal] Failed to revoke mobile sessions:', revokeError);
      setError(revokeError instanceof Error ? revokeError.message : 'Unable to sign out the extra phones or tablets.');
    } finally {
      setBulkRevoking(false);
    }
  }, [loadSessions, mobileSessions, revokeSessions]);

  const handleRemoveAllTrust = useCallback(async () => {
    if (trustedBrowsers.length === 0) {
      return;
    }

    const confirmed = await notify.confirm({
      title: 'Remove all trust',
      message: `Are you sure you want to remove trust from all ${trustedBrowsers.length} trusted browser${trustedBrowsers.length === 1 ? '' : 's'} or desktop app${trustedBrowsers.length === 1 ? '' : 's'}? You will need to complete MFA again on each of them next time you sign in.`,
      confirmText: 'Remove all trust',
      cancelText: 'Cancel',
      variant: 'danger',
    });

    if (!confirmed) {
      return;
    }

    setBulkRemovingTrust(true);
    setError(null);

    try {
      const results = await Promise.all(
        trustedBrowsers.map((browser) =>
          apiService.post<{ success: boolean; error?: string }>('/api/v1/sessions/active', {
            action: 'revoke_browser_trust',
            app_id: browser.app_id,
          })
        )
      );

      const failed = results.find((r) => !r.success);
      if (failed) {
        throw new Error(failed.error || 'Unable to remove trust from some browsers');
      }

      // If the current browser was trusted, clear remembered user data
      const currentBrowser = trustedBrowsers.find((b) => b.is_current);
      if (currentBrowser && loginHint) {
        clearRememberedUser(loginHint);
      }

      await loadSessions();
    } catch (revokeError) {
      console.error('[MyDevicesModal] Failed to remove all trust:', revokeError);
      setError(revokeError instanceof Error ? revokeError.message : 'Unable to remove trust from all browsers.');
    } finally {
      setBulkRemovingTrust(false);
    }
  }, [trustedBrowsers, loginHint, clearRememberedUser, loadSessions]);

  const handleSignOutFromAll = useCallback(async () => {
    if (sessions.length === 0) {
      return;
    }

    const otherSessionsCount = sessions.filter((s) => !s.is_current).length;
    const confirmed = await notify.confirm({
      title: 'Sign out from all',
      message: otherSessionsCount > 0
        ? `Are you sure you want to sign out from all ${sessions.length} device${sessions.length === 1 ? '' : 's'}? This includes the device you are currently using. You will need to sign in again to access your account.`
        : 'Are you sure you want to sign out? You will need to sign in again to access your account.',
      confirmText: 'Sign out from all',
      cancelText: 'Cancel',
      variant: 'danger',
    });

    if (!confirmed) {
      return;
    }

    setBulkSigningOutAll(true);
    setError(null);

    try {
      // Revoke all non-current sessions
      const otherSessionIds = sessions.filter((s) => !s.is_current).map((s) => s.session_id);
      if (otherSessionIds.length > 0) {
        await revokeSessions(otherSessionIds);
      }

      // Clear remembered user data before logging out
      if (loginHint) {
        clearRememberedUser(loginHint);
      }

      // Logout the current session
      await logout();
    } catch (revokeError) {
      console.error('[MyDevicesModal] Failed to sign out from all:', revokeError);
      setError(revokeError instanceof Error ? revokeError.message : 'Unable to sign out from all devices.');
      setBulkSigningOutAll(false);
    }
  }, [sessions, revokeSessions, loginHint, clearRememberedUser, logout]);

  const handleCopySetupLink = useCallback(async () => {
    const linkToCopy = mobileSetupUrl || fallbackMobileSetupUrl;
    if (!linkToCopy || typeof navigator === 'undefined' || !navigator.clipboard) {
      return;
    }

    try {
      await navigator.clipboard.writeText(linkToCopy);
      setCopiedSetupLink(true);
      window.setTimeout(() => setCopiedSetupLink(false), 2000);
    } catch (copyError) {
      console.error('[MyDevicesModal] Failed to copy mobile setup link:', copyError);
    }
  }, [fallbackMobileSetupUrl, mobileSetupUrl]);

  const handleRemoveTrustedBrowser = useCallback(async (appId: string) => {
    setRevokingTrustedBrowserIds((current) => [...current, appId]);
    setError(null);

    try {
      const browserToRemove = trustedBrowsers.find((b) => b.app_id === appId);
      const isRemovingCurrentBrowser = browserToRemove?.is_current;

      const response = await apiService.post<{ success: boolean; error?: string }>('/api/v1/sessions/active', {
        action: 'revoke_browser_trust',
        app_id: appId,
      });

      if (!response.success) {
        throw new Error(response.error || 'Unable to remove browser trust');
      }

      // If removing trust from the current browser, clear remembered user data
      if (isRemovingCurrentBrowser && loginHint) {
        clearRememberedUser(loginHint);
      }

      await loadSessions();
    } catch (revokeError) {
      console.error('[MyDevicesModal] Failed to revoke trusted browser:', revokeError);
      setError(revokeError instanceof Error ? revokeError.message : 'Unable to remove trust from that browser.');
    } finally {
      setRevokingTrustedBrowserIds((current) => current.filter((id) => id !== appId));
    }
  }, [loadSessions, trustedBrowsers, loginHint, clearRememberedUser]);

  const handleRequireMfaNextSignIn = useCallback(async (appId: string) => {
    setRevokingNativeTrustAppIds((current) => [...current, appId]);
    setError(null);

    try {
      const response = await apiService.post<{ success: boolean; error?: string }>('/api/v1/sessions/active', {
        action: 'revoke_mfa_trust',
        app_id: appId,
      });

      if (!response.success) {
        throw new Error(response.error || 'Unable to require MFA for that mobile app');
      }

      await loadSessions();
    } catch (revokeError) {
      console.error('[MyDevicesModal] Failed to require MFA for mobile app:', revokeError);
      setError(revokeError instanceof Error ? revokeError.message : 'Unable to require MFA for that mobile app.');
    } finally {
      setRevokingNativeTrustAppIds((current) => current.filter((id) => id !== appId));
    }
  }, [loadSessions]);

  const currentTrustedBrowser = useMemo(
    () => trustedBrowsers.find((browser) => browser.is_current) || null,
    [trustedBrowsers]
  );

  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }

    setPortalContainer(document.body);
  }, []);

  if (!isOpen) {
    return null;
  }

  const pageContent = (
    <div className="px-2 pb-1 pt-2 sm:px-4 sm:pb-3 sm:pt-4">
      <div className="mb-3 sm:mb-6 px-2 relative">
        <div className="text-center">
          <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-lg sm:mb-3 sm:h-16 sm:w-16">
            <MonitorSmartphone className="h-6 w-6 sm:h-8 sm:w-8" />
          </div>
          <h1 className="pm-heading-1 text-lg sm:text-2xl mb-1">My Devices</h1>
        </div>
        <div className="absolute right-2 top-0 flex items-center gap-2">
          <UserDocsLink
            docsPath="/getting-started/my-corner#my-devices"
            label="User guide: My Devices"
          />
          {onClose && (
            <button
              onClick={onClose}
              className="inline-flex items-center justify-center rounded-lg border border-gray-200 bg-white p-2 text-gray-400 transition-colors hover:border-gray-300 hover:text-gray-600 dark:border-gray-700 dark:bg-gray-900/50 dark:text-gray-500 dark:hover:border-gray-600 dark:hover:text-gray-300"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-2 px-2 md:grid-cols-2 sm:gap-4">
        <div className="bg-white dark:bg-gray-800 p-3 sm:p-5 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm md:col-span-2">
          <h3 className="flex items-center text-base sm:text-lg font-semibold mb-2 pm-text-primary">
            <Smartphone className="w-4 h-4 mr-1.5 text-blue-600 dark:text-blue-400" />
            Set up this device
          </h3>
          <div className="flex flex-wrap items-center gap-2">
            {(showAndroidInstallCard || showAppGalleryInstallCard || showQrHandoffCard) && (
              <div className="inline-flex items-center gap-2 rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
                <CheckCircle2 className="h-4 w-4" />
                App recommended
              </div>
            )}
            {(showInstalledWebAppCard || showContinueOnPhoneCard || isStandalonePwaInstalled) && (
              <div className="inline-flex items-center gap-2 rounded-full bg-violet-100 px-3 py-1 text-xs font-semibold text-violet-700 dark:bg-violet-900/30 dark:text-violet-300">
                <MonitorSmartphone className="h-4 w-4" />
                Easy access available
              </div>
            )}
            <div className="inline-flex items-center gap-2 rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
              <Globe className="h-4 w-4" />
              Browser access stays separate
            </div>
          </div>

          <div className="mt-2 rounded-lg border border-gray-200 bg-slate-50 px-3 py-2 dark:border-gray-700 dark:bg-slate-900/60">
            <p className="text-sm font-semibold text-slate-900 dark:text-white">{installSectionTitle}</p>
            <p className="mt-1 text-xs sm:text-sm text-slate-600 dark:text-slate-300">{installSectionDescription}</p>
          </div>

          <div className={`mt-2 grid gap-3 ${(showQrHandoffCard || showContinueOnThisDeviceCard) ? '' : 'md:grid-cols-1'}`}>
            {showAndroidInstallCard && (
              <button
                type="button"
                onClick={onOpenPlayStore}
                className="group rounded-lg border border-emerald-200 bg-gradient-to-br from-emerald-50 to-cyan-50 p-3 text-left transition hover:-translate-y-0.5 hover:shadow-md dark:border-emerald-900/70 dark:from-emerald-950/40 dark:to-cyan-950/20"
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-emerald-700 dark:text-emerald-300">Recommended option</p>
                    <h3 className="mt-1 text-base font-bold text-slate-900 dark:text-white">Install the app</h3>
                    <p className="mt-1 text-xs sm:text-sm text-slate-600 dark:text-slate-300">Open Google Play and install the TotalAccess app on this Android device.</p>
                  </div>
                  <div className="rounded-2xl bg-emerald-500 p-3 text-white shadow-lg">
                    <Smartphone className="h-5 w-5" />
                  </div>
                </div>
                <div className="mt-2 inline-flex items-center gap-2 text-sm font-semibold text-emerald-700 dark:text-emerald-300">
                  Open Google Play
                  <ExternalLink className="h-4 w-4 transition group-hover:translate-x-0.5" />
                </div>
              </button>
            )}

            {showAppGalleryInstallCard && (
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={onOpenAppGallery ?? (() => { window.location.href = '/appgallery'; })}
                  className="group w-full rounded-lg border border-red-200 bg-gradient-to-br from-red-50 to-orange-50 p-3 text-left transition hover:-translate-y-0.5 hover:shadow-md dark:border-red-900/70 dark:from-red-950/40 dark:to-orange-950/20"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-red-700 dark:text-red-300">Recommended option</p>
                      <h3 className="mt-1 text-base font-bold text-slate-900 dark:text-white">Install the app</h3>
                      <p className="mt-1 text-xs sm:text-sm text-slate-600 dark:text-slate-300">Open AppGallery and install the TotalAccess app on this Huawei device.</p>
                    </div>
                    <div className="rounded-2xl bg-red-500 p-3 text-white shadow-lg">
                      <Smartphone className="h-5 w-5" />
                    </div>
                  </div>
                  <div className="mt-2 inline-flex items-center gap-2 text-sm font-semibold text-red-700 dark:text-red-300">
                    Open AppGallery
                    <ExternalLink className="h-4 w-4 transition group-hover:translate-x-0.5" />
                  </div>
                </button>
                <a
                  href={HUAWEI_APK_DOWNLOAD_URL}
                  download
                  className="group block rounded-lg border border-slate-200 bg-slate-50 p-3 text-left transition hover:-translate-y-0.5 hover:shadow-md dark:border-slate-700 dark:bg-slate-900/40"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-slate-600 dark:text-slate-400">Alternative option</p>
                      <h3 className="mt-1 text-base font-bold text-slate-900 dark:text-white">Direct APK download</h3>
                      <p className="mt-1 text-xs sm:text-sm text-slate-600 dark:text-slate-300">Download the APK directly and sideload it onto this Huawei device.</p>
                    </div>
                    <div className="rounded-2xl bg-slate-600 p-3 text-white shadow-lg">
                      <Download className="h-5 w-5" />
                    </div>
                  </div>
                  <div className="mt-2 inline-flex items-center gap-2 text-sm font-semibold text-slate-600 dark:text-slate-400">
                    Download APK
                    <Download className="h-4 w-4 transition group-hover:translate-y-0.5" />
                  </div>
                </a>
              </div>
            )}

            {showAndroidFallbackAction && (
              <button
                type="button"
                onClick={onOpenPwaInstall}
                className="group rounded-lg border border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 p-3 text-left transition hover:-translate-y-0.5 hover:shadow-md dark:border-amber-900/70 dark:from-amber-950/30 dark:to-orange-950/20"
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-amber-700 dark:text-amber-300">
                      {showAppGalleryInstallCard ? 'If AppGallery does not open' : 'If Google Play does not open'}
                    </p>
                    <h3 className="mt-1 text-base font-bold text-slate-900 dark:text-white">Use the web app instead</h3>
                    <p className="mt-1 text-xs sm:text-sm text-slate-600 dark:text-slate-300">
                      {showAppGalleryInstallCard
                        ? 'If AppGallery is unavailable on this device, continue with the signed-in web app install instead.'
                        : 'If Google Play is unavailable on this device, continue with the signed-in web app install instead.'}
                    </p>
                  </div>
                  <div className="rounded-2xl bg-amber-500 p-3 text-white shadow-lg">
                    <Download className="h-5 w-5" />
                  </div>
                </div>
                <div className="mt-2 inline-flex items-center gap-2 text-sm font-semibold text-amber-700 dark:text-amber-300">
                  Use web app
                  <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
                </div>
              </button>
            )}

            {showInstalledWebAppCard && (
              <button
                type="button"
                onClick={onOpenPwaInstall}
                className="group rounded-lg border border-violet-200 bg-gradient-to-br from-violet-50 to-fuchsia-50 p-3 text-left transition hover:-translate-y-0.5 hover:shadow-md dark:border-violet-900/70 dark:from-violet-950/40 dark:to-fuchsia-950/20"
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-violet-700 dark:text-violet-300">Recommended option</p>
                    <h3 className="mt-1 text-base font-bold text-slate-900 dark:text-white">{installedWebAppCardTitle}</h3>
                    <p className="mt-1 text-xs sm:text-sm text-slate-600 dark:text-slate-300">{installedWebAppCardDescription}</p>
                  </div>
                  <div className="rounded-2xl bg-violet-500 p-3 text-white shadow-lg">
                    <Download className="h-5 w-5" />
                  </div>
                </div>
                <div className="mt-2 inline-flex items-center gap-2 text-sm font-semibold text-violet-700 dark:text-violet-300">
                  {installedWebAppCardAction}
                  <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
                </div>
              </button>
            )}

            {showContinueOnThisDeviceCard && (
              <button
                type="button"
                onClick={onOpenPwaInstall}
                className="group rounded-lg border border-cyan-200 bg-gradient-to-br from-cyan-50 to-blue-50 p-3 text-left transition hover:-translate-y-0.5 hover:shadow-md dark:border-cyan-900/70 dark:from-cyan-950/30 dark:to-blue-950/20"
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-cyan-700 dark:text-cyan-300">Recommended option</p>
                    <h3 className="mt-1 text-base font-bold text-slate-900 dark:text-white">Continue on this device</h3>
                    <p className="mt-1 text-xs sm:text-sm text-slate-600 dark:text-slate-300">You are already signed in here, so you can finish setup on this device without scanning a QR code.</p>
                  </div>
                  <div className="rounded-2xl bg-cyan-500 p-3 text-white shadow-lg">
                    <MonitorSmartphone className="h-5 w-5" />
                  </div>
                </div>
                <div className="mt-2 inline-flex items-center gap-2 text-sm font-semibold text-cyan-700 dark:text-cyan-300">
                  Continue setup
                  <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
                </div>
              </button>
            )}

            {showRedirectToAppHostCard && (
              <a
                href={installContext.href}
                className="group rounded-lg border border-violet-200 bg-gradient-to-br from-violet-50 to-purple-50 p-3 text-left transition hover:-translate-y-0.5 hover:shadow-md dark:border-violet-900/70 dark:from-violet-950/30 dark:to-purple-950/20"
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-violet-700 dark:text-violet-300">Required for iPhone/iPad</p>
                    <h3 className="mt-1 text-base font-bold text-slate-900 dark:text-white">Continue to app.totalaccess.co.za</h3>
                    <p className="mt-1 text-xs sm:text-sm text-slate-600 dark:text-slate-300">
                      The PWA must be installed from the app domain so it opens in standalone mode (required for clock-in). You will be redirected there now.
                    </p>
                  </div>
                  <div className="rounded-2xl bg-violet-500 p-3 text-white shadow-lg">
                    <ExternalLink className="h-5 w-5" />
                  </div>
                </div>
                <div className="mt-2 inline-flex items-center gap-2 text-sm font-semibold text-violet-700 dark:text-violet-300">
                  Go to install page
                  <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
                </div>
              </a>
            )}

            {showQrHandoffCard && (
              <div className="rounded-lg border border-slate-200 bg-gradient-to-br from-white to-slate-50 p-3 text-left dark:border-slate-700 dark:from-slate-900 dark:to-slate-950">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-slate-700 dark:text-slate-300">Continue on your phone</p>
                    <h3 className="mt-1 text-lg font-bold text-slate-900 dark:text-white">Finish setup on your phone</h3>
                    <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">Open TotalAccess on your phone and we&apos;ll guide you from there.</p>
                  </div>
                  <div className="rounded-2xl bg-slate-800 p-3 text-white shadow-lg dark:bg-slate-700">
                    <MonitorSmartphone className="h-5 w-5" />
                  </div>
                </div>

                <div className="mt-4 rounded-lg border border-slate-200 bg-white/80 px-3 py-2 text-sm text-slate-600 dark:border-slate-700 dark:bg-slate-900/70 dark:text-slate-300">
                  {installContext.helperText}
                </div>

                <div className="mt-2 grid gap-3 lg:grid-cols-[200px_minmax(0,1fr)]">
                  <div className="rounded-lg border border-cyan-200 bg-white p-3 shadow-sm dark:border-cyan-900/60 dark:bg-slate-900">
                    <div className="flex items-center justify-center rounded-2xl bg-white p-2">
                      <QRCodeCanvas value={mobileSetupUrl || fallbackMobileSetupUrl || 'https://totalaccess.co.za/client-zone/my-devices'} size={160} level="M" includeMargin={true} bgColor="#ffffff" fgColor="#0f172a" />
                    </div>
                    <p className="mt-2 text-center text-xs font-medium text-slate-600 dark:text-slate-300">{generatingSecureLink ? 'Preparing your phone link…' : 'Scan with your phone camera'}</p>
                  </div>

                  <div className="rounded-lg border border-dashed border-cyan-200 bg-cyan-50/80 p-3 dark:border-cyan-900/60 dark:bg-cyan-950/20">
                    <p className="text-sm font-semibold text-slate-900 dark:text-white">Open TotalAccess on your phone</p>
                    <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">Scan the QR code to open TotalAccess on your phone and continue from there. When available, we&apos;ll sign you in automatically so setup is quicker.</p>
                    {secureLinkError && <p className="mt-3 text-sm text-amber-700 dark:text-amber-300">{secureLinkError}</p>}
                    {loginHint && (
                      <p className="mt-3 text-sm text-slate-600 dark:text-slate-300">
                        If the quick link has expired, we&apos;ll still open the sign-in page with <span className="font-semibold text-slate-900 dark:text-white">{loginHint}</span> filled in.
                      </p>
                    )}
                    <div className="mt-3 flex flex-wrap gap-2">
                      <a href={mobileSetupUrl || fallbackMobileSetupUrl || '/client-zone/my-devices'} className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700">
                        {generatingSecureLink ? <Loader2 className="h-4 w-4 animate-spin" /> : <ExternalLink className="h-4 w-4" />}
                        {generatingSecureLink ? 'Preparing link' : 'Open on phone'}
                      </a>
                      <button type="button" onClick={() => void handleCopySetupLink()} className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 dark:border-gray-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800">
                        <Copy className="h-4 w-4" />
                        {copiedSetupLink ? 'Link copied' : 'Copy link'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-3 sm:p-5 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
          <h3 className="flex items-center text-base sm:text-lg font-semibold mb-2 pm-text-primary">
            <MonitorSmartphone className="w-4 h-4 mr-1.5 text-blue-600 dark:text-blue-400" />
            This device
          </h3>
          <div className="space-y-2 text-sm text-slate-600 dark:text-slate-300">
            <div className="flex items-start justify-between gap-3 rounded-lg bg-slate-50 px-3 py-2 dark:bg-slate-800/80">
              <span className="font-semibold text-slate-700 dark:text-slate-100">Added to this device</span>
              <span className={`font-semibold ${isStandalonePwaInstalled ? 'text-emerald-600 dark:text-emerald-300' : 'text-slate-500 dark:text-slate-400'}`}>{isStandalonePwaInstalled ? 'Yes' : 'No'}</span>
            </div>
            <div className="flex items-start justify-between gap-3 rounded-lg bg-slate-50 px-3 py-2 dark:bg-slate-800/80">
              <span className="font-semibold text-slate-700 dark:text-slate-100">Device code</span>
              <span className="max-w-[180px] break-all text-right font-mono text-xs text-slate-600 dark:text-slate-300">{userAppId || currentSession?.app_id || 'Not linked'}</span>
            </div>
            <div className="flex items-start justify-between gap-3 rounded-lg bg-slate-50 px-3 py-2 dark:bg-slate-800/80">
              <span className="font-semibold text-slate-700 dark:text-slate-100">You&apos;re using TotalAccess in</span>
              <span className="font-semibold text-slate-700 dark:text-slate-200">{currentSession ? sessionKindMeta[currentSession.session_kind].label : 'Browser'}</span>
            </div>
            <div className="flex items-start justify-between gap-3 rounded-lg bg-slate-50 px-3 py-2 dark:bg-slate-800/80">
              <span className="font-semibold text-slate-700 dark:text-slate-100">{currentSession?.session_kind === 'native_app' ? 'MFA on this mobile app' : 'MFA trust on this browser'}</span>
              <span className={`text-right font-semibold ${currentSession?.session_kind === 'native_app'
                ? currentSession.mfa_trust_disabled
                  ? 'text-amber-600 dark:text-amber-300'
                  : 'text-emerald-600 dark:text-emerald-300'
                : currentTrustedBrowser
                  ? 'text-emerald-600 dark:text-emerald-300'
                  : 'text-slate-500 dark:text-slate-400'}`}>
                {currentSession?.session_kind === 'native_app'
                  ? currentSession.mfa_trust_disabled
                    ? 'Required next sign-in'
                    : 'Only if this app changes'
                  : currentTrustedBrowser
                    ? `Until ${formatSessionDate(currentTrustedBrowser.trusted_until)}`
                    : 'Not trusted'}
              </span>
            </div>
          </div>
          {showContinueOnPhoneCard && !isStandalonePwaInstalled && (
            <div className="mt-3 rounded-lg border border-blue-200 bg-blue-50/60 px-3 py-2.5 dark:border-blue-900/60 dark:bg-blue-950/20">
              <p className="text-xs text-slate-600 dark:text-slate-300">
                On a Windows or Mac desktop? Download the Total Access desktop app for WiFi geofencing and biometric clock-in.
              </p>
              <Link href="/resources/downloads#software" className="mt-1.5 inline-flex items-center gap-1.5 text-sm font-semibold text-blue-600 dark:text-blue-400 hover:underline">
                <Download className="h-4 w-4" />
                Get desktop software
                <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          )}
        </div>

        <div className="bg-white dark:bg-gray-800 p-3 sm:p-5 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
          <h3 className="flex items-center text-base sm:text-lg font-semibold mb-2 pm-text-primary">
            <Shield className="w-4 h-4 mr-1.5 text-blue-600 dark:text-blue-400" />
            Signed-in devices
          </h3>
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <button type="button" onClick={() => void loadSessions()} disabled={loading} className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs sm:text-sm font-semibold text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800">
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button type="button" onClick={() => void handleSignOutOtherMobileDevices()} disabled={bulkRevoking || mobileSessions.filter((session) => !session.is_current).length === 0} className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-2.5 py-1.5 text-xs sm:text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60">
              {bulkRevoking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              Sign out other phones/tablets
            </button>
            <button type="button" onClick={() => void handleSignOutFromAll()} disabled={bulkSigningOutAll || bulkRevoking || sessions.length === 0} className="inline-flex items-center gap-2 rounded-lg bg-rose-600 px-2.5 py-1.5 text-xs sm:text-sm font-semibold text-white shadow-sm transition hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-60">
              {bulkSigningOutAll ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              Sign out from all
            </button>
          </div>

          <p className="mb-2 text-xs sm:text-sm text-slate-600 dark:text-slate-300">
            Capacitor mobile apps stay trusted for MFA until their app ID changes. You can force MFA on the next sign-in for any mobile app below.
          </p>

          {error && <div className="mb-2 rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700 dark:border-rose-900/60 dark:bg-rose-950/30 dark:text-rose-300">{error}</div>}

          {hasMobileConflict && (
            <div className="mb-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-100">
              <div className="flex items-start gap-3">
                <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                <div>
                  <p className="font-semibold">More than one phone or tablet is signed in</p>
                  <p className="mt-1 leading-relaxed">Keep the one you want to use, then sign out the others so your account stays linked to the right device.</p>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-2">
            {loading ? (
              <div className="flex min-h-[120px] items-center justify-center rounded-lg border border-dashed border-slate-200 bg-slate-50/80 dark:border-slate-700 dark:bg-slate-800/50">
                <div className="flex items-center gap-2 text-xs sm:text-sm font-medium text-slate-600 dark:text-slate-300"><Loader2 className="h-5 w-5 animate-spin" />Loading your devices…</div>
              </div>
            ) : sessions.length === 0 ? (
              <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50/80 p-4 text-center text-sm text-slate-600 dark:border-slate-700 dark:bg-slate-800/50 dark:text-slate-300">We haven&apos;t found any signed-in devices for this account yet.</div>
            ) : (
              sessions.map((session) => {
                const meta = sessionKindMeta[session.session_kind] || sessionKindMeta.portal;
                const SessionIcon = meta.icon;
                const isRevoking = revokingSessionIds.includes(session.session_id);
                const nativeTrustAppId = session.session_kind === 'native_app' ? session.app_id : null;
                const isRevokingNativeTrust = session.app_id ? revokingNativeTrustAppIds.includes(session.app_id) : false;

                return (
                  <div key={session.session_id} className={`rounded-lg border p-3 transition ${session.is_current ? 'border-emerald-200 bg-emerald-50/70 dark:border-emerald-900/60 dark:bg-emerald-950/20' : 'border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900/70'}`}>
                    <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${meta.badgeClass}`}><SessionIcon className="h-3.5 w-3.5" />{session.platform_label || meta.label}</span>
                          {session.is_current && <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300"><CheckCircle2 className="h-3.5 w-3.5" />This device</span>}
                          {session.has_push_token && <span className="inline-flex items-center gap-1.5 rounded-full bg-sky-100 px-3 py-1 text-xs font-semibold text-sky-700 dark:bg-sky-900/30 dark:text-sky-300"><MonitorSmartphone className="h-3.5 w-3.5" />Notifications on</span>}
                          {session.session_kind === 'native_app' && session.mfa_trust_disabled && <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-700 dark:bg-amber-900/30 dark:text-amber-300"><Shield className="h-3.5 w-3.5" />MFA next sign-in</span>}
                        </div>
                        <h4 className="mt-1 text-sm sm:text-base font-bold text-slate-900 dark:text-white">{session.device_name}</h4>
                        <div className="mt-1 grid gap-1 text-xs sm:text-sm text-slate-600 dark:text-slate-300 sm:grid-cols-2">
                          <div><span className="font-semibold text-slate-700 dark:text-slate-200">Last active:</span> {formatSessionDate(session.last_active)}</div>
                          <div><span className="font-semibold text-slate-700 dark:text-slate-200">Created:</span> {formatSessionDate(session.created_at)}</div>
                          <div className="sm:col-span-2"><span className="font-semibold text-slate-700 dark:text-slate-200">Device code:</span> <span className="font-mono text-xs break-all">{session.app_id || 'Not linked yet'}</span></div>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2 md:justify-end">
                        {nativeTrustAppId && !session.mfa_trust_disabled && (
                          <button
                            type="button"
                            onClick={() => void handleRequireMfaNextSignIn(nativeTrustAppId!)}
                            disabled={isRevokingNativeTrust}
                            className="inline-flex items-center gap-2 rounded-lg border border-amber-200 bg-white px-2.5 py-1.5 text-xs sm:text-sm font-semibold text-amber-700 transition hover:bg-amber-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-amber-900/60 dark:bg-slate-900 dark:text-amber-300 dark:hover:bg-amber-950/20"
                          >
                            {isRevokingNativeTrust ? <Loader2 className="h-4 w-4 animate-spin" /> : <Shield className="h-4 w-4" />}
                            Require MFA next sign-in
                          </button>
                        )}
                        <button type="button" onClick={() => void handleRevokeSession(session.session_id)} disabled={isRevoking} className="inline-flex items-center gap-2 rounded-lg border border-rose-200 bg-white px-2.5 py-1.5 text-xs sm:text-sm font-semibold text-rose-700 transition hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-rose-900/60 dark:bg-slate-900 dark:text-rose-300 dark:hover:bg-rose-950/20">
                          {isRevoking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}Sign out
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-3 sm:p-5 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
          <h3 className="flex items-center text-base sm:text-lg font-semibold mb-2 pm-text-primary">
            <Globe className="w-4 h-4 mr-1.5 text-blue-600 dark:text-blue-400" />
            Trusted browsers & desktop apps
          </h3>
          <p className="mb-2 text-xs sm:text-sm text-slate-600 dark:text-slate-300">
            These browsers and desktop apps can skip MFA for up to 30 days after you choose “Don&apos;t ask again” during MFA. Remove trust at any time.
          </p>

          {trustedBrowsers.length > 0 && (
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <button type="button" onClick={() => void handleRemoveAllTrust()} disabled={bulkRemovingTrust || revokingTrustedBrowserIds.length > 0} className="inline-flex items-center gap-2 rounded-lg bg-rose-600 px-2.5 py-1.5 text-xs sm:text-sm font-semibold text-white shadow-sm transition hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-60">
                {bulkRemovingTrust ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                Remove all trust
              </button>
            </div>
          )}

          <div className="space-y-2">
            {trustedBrowsers.length === 0 ? (
              <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50/80 p-4 text-center text-sm text-slate-600 dark:border-slate-700 dark:bg-slate-800/50 dark:text-slate-300">
                No trusted browsers or desktop apps yet. The next time you complete MFA on a browser or desktop app, you can choose to trust it for 30 days.
              </div>
            ) : (
              trustedBrowsers.map((browser) => {
                const isRevoking = revokingTrustedBrowserIds.includes(browser.app_id);

                return (
                  <div key={browser.browser_id} className={`rounded-lg border p-3 transition ${browser.is_current ? 'border-emerald-200 bg-emerald-50/70 dark:border-emerald-900/60 dark:bg-emerald-950/20' : 'border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900/70'}`}>
                    <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                            <Globe className="h-3.5 w-3.5" />
                            {browser.browser_label}
                          </span>
                          {browser.is_current && (
                            <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
                              <CheckCircle2 className="h-3.5 w-3.5" />
                              This browser
                            </span>
                          )}
                        </div>
                        <h4 className="mt-1 text-sm sm:text-base font-bold text-slate-900 dark:text-white">{browser.device_name}</h4>
                        <div className="mt-1 grid gap-1 text-xs sm:text-sm text-slate-600 dark:text-slate-300 sm:grid-cols-2">
                          <div><span className="font-semibold text-slate-700 dark:text-slate-200">Trusted on:</span> {formatSessionDate(browser.trusted_at)}</div>
                          <div><span className="font-semibold text-slate-700 dark:text-slate-200">Trust ends:</span> {formatSessionDate(browser.trusted_until)}</div>
                          <div className="sm:col-span-2"><span className="font-semibold text-slate-700 dark:text-slate-200">Browser code:</span> <span className="font-mono text-xs break-all">{browser.app_id}</span></div>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2 md:justify-end">
                        <button
                          type="button"
                          onClick={() => void handleRemoveTrustedBrowser(browser.app_id)}
                          disabled={isRevoking}
                          className="inline-flex items-center gap-2 rounded-lg border border-rose-200 bg-white px-2.5 py-1.5 text-xs sm:text-sm font-semibold text-rose-700 transition hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-rose-900/60 dark:bg-slate-900 dark:text-rose-300 dark:hover:bg-rose-950/20"
                        >
                          {isRevoking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                          Remove trust
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-3 sm:p-5 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
          <h3 className="flex items-center text-base sm:text-lg font-semibold mb-2 pm-text-primary">
            <Activity className="w-4 h-4 mr-1.5 text-blue-600 dark:text-blue-400" />
            Summary
          </h3>
          <div className="grid grid-cols-1 gap-2">
            <div className="rounded-lg bg-gradient-to-br from-emerald-50 to-cyan-50 p-3 dark:from-emerald-950/20 dark:to-cyan-950/20">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-emerald-700 dark:text-emerald-300">Phones / tablets</p>
              <p className="mt-1 text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white">{mobileSessions.length}</p>
            </div>
            <div className="rounded-lg bg-gradient-to-br from-blue-50 to-indigo-50 p-3 dark:from-blue-950/20 dark:to-indigo-950/20">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-blue-700 dark:text-blue-300">Browser sign-ins</p>
              <p className="mt-1 text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white">{portalSessions.length}</p>
            </div>
            <div className="rounded-lg bg-gradient-to-br from-cyan-50 to-sky-50 p-3 dark:from-cyan-950/20 dark:to-sky-950/20">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-cyan-700 dark:text-cyan-300">Trusted browsers</p>
              <p className="mt-1 text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white">{trustedBrowsers.length}</p>
            </div>
            <div className="rounded-lg bg-gradient-to-br from-slate-100 to-slate-50 p-3 dark:from-slate-800 dark:to-slate-900">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-700 dark:text-slate-300">Desktop sign-ins</p>
              <p className="mt-1 text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white">{desktopSessions.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-3 sm:p-5 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
          <h3 className="flex items-center text-base sm:text-lg font-semibold mb-2 pm-text-primary">
            <CheckCircle2 className="w-4 h-4 mr-1.5 text-blue-600 dark:text-blue-400" />
            What we recommend
          </h3>
          <ol className="space-y-2 text-xs sm:text-sm text-slate-600 dark:text-slate-300">
            {showAndroidInstallCard && <li className="flex gap-2"><span className="mt-0.5 inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-emerald-500 text-[10px] font-bold text-white">1</span><span>Install the TotalAccess app on this Android device for the smoothest experience.</span></li>}
            {showAndroidFallbackAction && <li className="flex gap-2"><span className="mt-0.5 inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-amber-500 text-[10px] font-bold text-white">2</span><span>If Google Play is not available on this device, continue with the signed-in web app install instead.</span></li>}
            {showInstalledWebAppCard && <li className="flex gap-2"><span className="mt-0.5 inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-violet-500 text-[10px] font-bold text-white">1</span><span>{installedWebAppFlowLabel}</span></li>}
            {showQrHandoffCard && <li className="flex gap-2"><span className="mt-0.5 inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-slate-500 text-[10px] font-bold text-white">1</span><span>Open TotalAccess on your phone to finish setup there.</span></li>}
            {showContinueOnThisDeviceCard && <li className="flex gap-2"><span className="mt-0.5 inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-cyan-500 text-[10px] font-bold text-white">1</span><span>Continue setup on this device because you are already signed in here.</span></li>}
            {showRedirectToAppHostCard && <li className="flex gap-2"><span className="mt-0.5 inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-violet-500 text-[10px] font-bold text-white">1</span><span>Continue to the app domain to install the PWA — it must be installed from app.totalaccess.co.za to open in standalone mode.</span></li>}
            {showRedirectToAppHostCard && <li className="flex gap-2"><span className="mt-0.5 inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-violet-400 text-[10px] font-bold text-white">2</span><span>On the app domain, tap Share → Add to Home Screen to install the PWA in standalone mode.</span></li>}
            <li className="flex gap-2"><span className="mt-0.5 inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-rose-500 text-[10px] font-bold text-white">{showAndroidFallbackAction ? '3' : '2'}</span><span>After switching devices, sign out the extra phone or tablet here so your account stays linked to the right one.</span></li>
            <li className="flex gap-2"><span className="mt-0.5 inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-blue-500 text-[10px] font-bold text-white">{showAndroidFallbackAction ? '4' : '3'}</span><span>Review trusted browsers every so often and remove any you no longer use.</span></li>
          </ol>
        </div>
      </div>
    </div>
  );

  const content = (
    <>
      <div className="pm-gradient-hero px-4 py-3 text-white sm:px-7 sm:py-5">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <div className="mb-2 flex flex-wrap items-center gap-2">
              {isPage && (
                <Link
                  href={backHref}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-2xl border border-white/20 bg-white/10 text-white transition hover:bg-white/20"
                  aria-label="Back to dashboard"
                >
                  <ArrowLeft className="h-5 w-5" />
                </Link>
              )}
              <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/90">
                <Shield className="h-3.5 w-3.5" />
                My Devices
              </div>
            </div>
            <h2 className="pm-heading-1 !mb-0 text-xl sm:text-2xl text-white">Access TotalAccess on your devices</h2>
            <p className="mt-1 max-w-3xl text-xs text-white/85 sm:text-sm">
              Set up TotalAccess on this device and manage the devices linked to your account.
            </p>
          </div>

          {!isPage && onClose && (
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-9 w-9 items-center justify-center rounded-2xl border border-white/20 bg-white/10 text-white transition hover:bg-white/20"
              aria-label="Close My Devices"
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>
      </div>

      <div className={isPage ? 'pm-gradient-hero px-4 py-4 sm:p-6' : 'min-h-0 flex-1 overflow-y-auto pm-gradient-hero px-3 py-3 sm:p-5'}>
        <div className="grid gap-3 sm:gap-4 lg:grid-cols-[1.2fr_0.8fr]">
          <section className="space-y-3">
            <div className="pm-card !p-3 sm:!p-4 !shadow-sm hover:!transform-none hover:!shadow-sm">
              <div className="flex flex-wrap items-center gap-2">
                {(showAndroidInstallCard || showQrHandoffCard) && (
                  <div className="inline-flex items-center gap-2 rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
                    <CheckCircle2 className="h-4 w-4" />
                    App recommended
                  </div>
                )}
                {(showInstalledWebAppCard || showContinueOnPhoneCard || isStandalonePwaInstalled) && (
                  <div className="inline-flex items-center gap-2 rounded-full bg-violet-100 px-3 py-1 text-xs font-semibold text-violet-700 dark:bg-violet-900/30 dark:text-violet-300">
                    <MonitorSmartphone className="h-4 w-4" />
                    Easy access available
                  </div>
                )}
                <div className="inline-flex items-center gap-2 rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                  <Globe className="h-4 w-4" />
                  Browser access stays separate
                </div>
              </div>

              <div className="mt-2 rounded-lg border border-gray-200 bg-slate-50 px-3 py-2 dark:border-gray-700 dark:bg-slate-900/60">
                <p className="text-sm font-semibold text-slate-900 dark:text-white">{installSectionTitle}</p>
                <p className="mt-1 text-xs sm:text-sm text-slate-600 dark:text-slate-300">{installSectionDescription}</p>
              </div>

              <div className={`mt-2 grid gap-3 ${(showQrHandoffCard || showContinueOnThisDeviceCard) ? '' : 'md:grid-cols-1'}`}>
                {showAndroidInstallCard && (
                  <button
                    type="button"
                    onClick={onOpenPlayStore}
                    className="group rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-cyan-50 p-3 text-left transition hover:-translate-y-0.5 hover:shadow-md dark:border-emerald-900/70 dark:from-emerald-950/40 dark:to-cyan-950/20"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-sm font-semibold text-emerald-700 dark:text-emerald-300">Recommended option</p>
                        <h3 className="mt-1 text-base font-bold text-slate-900 dark:text-white">Install the app</h3>
                        <p className="mt-1 text-xs sm:text-sm text-slate-600 dark:text-slate-300">
                          Open Google Play and install the TotalAccess app on this Android device.
                        </p>
                      </div>
                      <div className="rounded-2xl bg-emerald-500 p-3 text-white shadow-lg">
                        <Smartphone className="h-5 w-5" />
                      </div>
                    </div>
                    <div className="mt-2 inline-flex items-center gap-2 text-sm font-semibold text-emerald-700 dark:text-emerald-300">
                      Open Google Play
                      <ExternalLink className="h-4 w-4 transition group-hover:translate-x-0.5" />
                    </div>
                  </button>
                )}

                {showAndroidFallbackAction && (
                  <button
                    type="button"
                    onClick={onOpenPwaInstall}
                    className="group rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 p-3 text-left transition hover:-translate-y-0.5 hover:shadow-md dark:border-amber-900/70 dark:from-amber-950/30 dark:to-orange-950/20"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-sm font-semibold text-amber-700 dark:text-amber-300">If Google Play does not open</p>
                        <h3 className="mt-1 text-base font-bold text-slate-900 dark:text-white">Use the web app instead</h3>
                        <p className="mt-1 text-xs sm:text-sm text-slate-600 dark:text-slate-300">
                          If Google Play is unavailable on this device, continue with the signed-in web app install instead.
                        </p>
                      </div>
                      <div className="rounded-2xl bg-amber-500 p-3 text-white shadow-lg">
                        <Download className="h-5 w-5" />
                      </div>
                    </div>
                    <div className="mt-2 inline-flex items-center gap-2 text-sm font-semibold text-amber-700 dark:text-amber-300">
                      Use web app
                      <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
                    </div>
                  </button>
                )}

                {showInstalledWebAppCard && (
                  <button
                    type="button"
                    onClick={onOpenPwaInstall}
                    className="group rounded-2xl border border-violet-200 bg-gradient-to-br from-violet-50 to-fuchsia-50 p-3 text-left transition hover:-translate-y-0.5 hover:shadow-md dark:border-violet-900/70 dark:from-violet-950/40 dark:to-fuchsia-950/20"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-sm font-semibold text-violet-700 dark:text-violet-300">Recommended option</p>
                        <h3 className="mt-1 text-base font-bold text-slate-900 dark:text-white">{installedWebAppCardTitle}</h3>
                        <p className="mt-1 text-xs sm:text-sm text-slate-600 dark:text-slate-300">
                          {installedWebAppCardDescription}
                        </p>
                      </div>
                      <div className="rounded-2xl bg-violet-500 p-3 text-white shadow-lg">
                        <Download className="h-5 w-5" />
                      </div>
                    </div>
                    <div className="mt-2 inline-flex items-center gap-2 text-sm font-semibold text-violet-700 dark:text-violet-300">
                      {installedWebAppCardAction}
                      <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
                    </div>
                  </button>
                )}

                {showContinueOnThisDeviceCard && (
                  <button
                    type="button"
                    onClick={onOpenPwaInstall}
                    className="group rounded-2xl border border-cyan-200 bg-gradient-to-br from-cyan-50 to-blue-50 p-3 text-left transition hover:-translate-y-0.5 hover:shadow-md dark:border-cyan-900/70 dark:from-cyan-950/30 dark:to-blue-950/20"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-sm font-semibold text-cyan-700 dark:text-cyan-300">Recommended option</p>
                        <h3 className="mt-1 text-base font-bold text-slate-900 dark:text-white">Continue on this device</h3>
                        <p className="mt-1 text-xs sm:text-sm text-slate-600 dark:text-slate-300">
                          You are already signed in here, so you can finish setup on this device without scanning a QR code.
                        </p>
                      </div>
                      <div className="rounded-2xl bg-cyan-500 p-3 text-white shadow-lg">
                        <MonitorSmartphone className="h-5 w-5" />
                      </div>
                    </div>
                    <div className="mt-2 inline-flex items-center gap-2 text-sm font-semibold text-cyan-700 dark:text-cyan-300">
                      Continue setup
                      <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
                    </div>
                  </button>
                )}

                {showRedirectToAppHostCard && (
                  <a
                    href={installContext.href}
                    className="group rounded-2xl border border-violet-200 bg-gradient-to-br from-violet-50 to-purple-50 p-3 text-left transition hover:-translate-y-0.5 hover:shadow-md dark:border-violet-900/70 dark:from-violet-950/30 dark:to-purple-950/20"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-sm font-semibold text-violet-700 dark:text-violet-300">Required for iPhone/iPad</p>
                        <h3 className="mt-1 text-base font-bold text-slate-900 dark:text-white">Continue to app.totalaccess.co.za</h3>
                        <p className="mt-1 text-xs sm:text-sm text-slate-600 dark:text-slate-300">
                          The PWA must be installed from the app domain so it opens in standalone mode (required for clock-in). You will be redirected there now.
                        </p>
                      </div>
                      <div className="rounded-2xl bg-violet-500 p-3 text-white shadow-lg">
                        <ExternalLink className="h-5 w-5" />
                      </div>
                    </div>
                    <div className="mt-2 inline-flex items-center gap-2 text-sm font-semibold text-violet-700 dark:text-violet-300">
                      Go to install page
                      <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
                    </div>
                  </a>
                )}

                {showQrHandoffCard && (
                  <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-white to-slate-50 p-3 text-left dark:border-slate-700 dark:from-slate-900 dark:to-slate-950">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-sm font-semibold text-slate-700 dark:text-slate-300">Continue on your phone</p>
                        <h3 className="mt-1 text-base font-bold text-slate-900 dark:text-white">Finish setup on your phone</h3>
                        <p className="mt-1 text-xs sm:text-sm text-slate-600 dark:text-slate-300">
                          Open TotalAccess on your phone and we&apos;ll guide you from there.
                        </p>
                      </div>
                      <div className="rounded-2xl bg-slate-800 p-3 text-white shadow-lg dark:bg-slate-700">
                        <MonitorSmartphone className="h-5 w-5" />
                      </div>
                    </div>

                    <div className="mt-2 rounded-xl border border-slate-200 bg-white/80 px-3 py-2 text-sm text-slate-600 dark:border-slate-700 dark:bg-slate-900/70 dark:text-slate-300">
                      {installContext.helperText}
                    </div>

                    <div className="mt-2 grid gap-3 lg:grid-cols-[200px_minmax(0,1fr)]">
                      <div className="rounded-2xl border border-cyan-200 bg-white p-3 shadow-sm dark:border-cyan-900/60 dark:bg-slate-900">
                        <div className="flex items-center justify-center rounded-2xl bg-white p-2">
                          <QRCodeCanvas
                            value={mobileSetupUrl || fallbackMobileSetupUrl || 'https://totalaccess.co.za/client-zone/my-devices'}
                            size={160}
                            level="M"
                            includeMargin={true}
                            bgColor="#ffffff"
                            fgColor="#0f172a"
                          />
                        </div>
                        <p className="mt-2 text-center text-xs font-medium text-slate-600 dark:text-slate-300">
                          {generatingSecureLink ? 'Preparing your phone link…' : 'Scan with your phone camera'}
                        </p>
                      </div>

                      <div className="rounded-2xl border border-dashed border-cyan-200 bg-cyan-50/80 p-3 dark:border-cyan-900/60 dark:bg-cyan-950/20">
                        <p className="text-sm font-semibold text-slate-900 dark:text-white">Open TotalAccess on your phone</p>
                        <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
                          Scan the QR code to open TotalAccess on your phone and continue from there. When available, we&apos;ll sign you in automatically so setup is quicker.
                        </p>
                        {secureLinkError && (
                          <p className="mt-3 text-sm text-amber-700 dark:text-amber-300">{secureLinkError}</p>
                        )}
                        {loginHint && (
                          <p className="mt-3 text-sm text-slate-600 dark:text-slate-300">
                            If the quick link has expired, we&apos;ll still open the sign-in page with <span className="font-semibold text-slate-900 dark:text-white">{loginHint}</span> filled in.
                          </p>
                        )}
                        <div className="mt-3 flex flex-wrap gap-2">
                          <a
                            href={mobileSetupUrl || fallbackMobileSetupUrl || '/client-zone/my-devices'}
                            className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-md transition hover:opacity-95"
                          >
                            {generatingSecureLink ? <Loader2 className="h-4 w-4 animate-spin" /> : <ExternalLink className="h-4 w-4" />}
                            {generatingSecureLink ? 'Preparing link' : 'Open on phone'}
                          </a>
                          <button
                            type="button"
                            onClick={() => void handleCopySetupLink()}
                            className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                          >
                            <Copy className="h-4 w-4" />
                            {copiedSetupLink ? 'Link copied' : 'Copy link'}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

            </div>

            <div className="pm-card !p-3 sm:!p-4 !shadow-sm hover:!transform-none hover:!shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <h3 className="text-base sm:text-lg font-bold text-slate-900 dark:text-white">Signed-in devices</h3>
                  <p className="mt-0.5 text-xs sm:text-sm text-slate-600 dark:text-slate-300">
                    See where your account is currently signed in.
                  </p>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => void loadSessions()}
                    disabled={loading}
                    className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-2.5 py-1.5 text-xs sm:text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
                  >
                    <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                    Refresh
                  </button>

                  <button
                    type="button"
                    onClick={() => void handleSignOutOtherMobileDevices()}
                    disabled={bulkRevoking || mobileSessions.filter((session) => !session.is_current).length === 0}
                    className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-rose-500 to-orange-500 px-2.5 py-1.5 text-xs sm:text-sm font-semibold text-white shadow-md transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {bulkRevoking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                    Sign out other phones/tablets
                  </button>

                  <button
                    type="button"
                    onClick={() => void handleSignOutFromAll()}
                    disabled={bulkSigningOutAll || bulkRevoking || sessions.length === 0}
                    className="inline-flex items-center gap-2 rounded-xl bg-rose-600 px-2.5 py-1.5 text-xs sm:text-sm font-semibold text-white shadow-md transition hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {bulkSigningOutAll ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                    Sign out from all
                  </button>
                </div>
              </div>

              {error && (
                <div className="mt-2 rounded-2xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700 dark:border-rose-900/60 dark:bg-rose-950/30 dark:text-rose-300">
                  {error}
                </div>
              )}

              {hasMobileConflict && (
                <div className="mt-2 rounded-2xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-100">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                    <div>
                      <p className="font-semibold">More than one phone or tablet is signed in</p>
                      <p className="mt-1 leading-relaxed">
                        Keep the one you want to use, then sign out the others so your account stays linked to the right device.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="mt-2 space-y-2">
                {loading ? (
                  <div className="flex min-h-[120px] items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-slate-50/80 dark:border-slate-700 dark:bg-slate-800/50">
                    <div className="flex items-center gap-2 text-xs sm:text-sm font-medium text-slate-600 dark:text-slate-300">
                      <Loader2 className="h-5 w-5 animate-spin" />
                      Loading your devices…
                    </div>
                  </div>
                ) : sessions.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50/80 p-4 text-center text-sm text-slate-600 dark:border-slate-700 dark:bg-slate-800/50 dark:text-slate-300">
                    We haven&apos;t found any signed-in devices for this account yet.
                  </div>
                ) : (
                  sessions.map((session) => {
                    const meta = sessionKindMeta[session.session_kind] || sessionKindMeta.portal;
                    const SessionIcon = meta.icon;
                    const isRevoking = revokingSessionIds.includes(session.session_id);

                    return (
                      <div
                        key={session.session_id}
                        className={`rounded-2xl border p-3 transition ${session.is_current
                          ? 'border-emerald-200 bg-emerald-50/70 dark:border-emerald-900/60 dark:bg-emerald-950/20'
                          : 'border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900/70'
                          }`}
                      >
                        <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${meta.badgeClass}`}>
                                <SessionIcon className="h-3.5 w-3.5" />
                                {session.platform_label || meta.label}
                              </span>
                              {session.is_current && (
                                <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
                                  <CheckCircle2 className="h-3.5 w-3.5" />
                                  This device
                                </span>
                              )}
                              {session.has_push_token && (
                                <span className="inline-flex items-center gap-1.5 rounded-full bg-sky-100 px-3 py-1 text-xs font-semibold text-sky-700 dark:bg-sky-900/30 dark:text-sky-300">
                                  <MonitorSmartphone className="h-3.5 w-3.5" />
                                  Notifications on
                                </span>
                              )}
                            </div>

                            <h4 className="mt-1 text-sm sm:text-base font-bold text-slate-900 dark:text-white">{session.device_name}</h4>

                            <div className="mt-1 grid gap-1 text-xs sm:text-sm text-slate-600 dark:text-slate-300 sm:grid-cols-2">
                              <div>
                                <span className="font-semibold text-slate-700 dark:text-slate-200">Last active:</span>{' '}
                                {formatSessionDate(session.last_active)}
                              </div>
                              <div>
                                <span className="font-semibold text-slate-700 dark:text-slate-200">Created:</span>{' '}
                                {formatSessionDate(session.created_at)}
                              </div>
                              <div className="sm:col-span-2">
                                <span className="font-semibold text-slate-700 dark:text-slate-200">Device code:</span>{' '}
                                <span className="font-mono text-xs break-all">{session.app_id || 'Not linked yet'}</span>
                              </div>
                            </div>
                          </div>

                          <div className="flex flex-wrap gap-2 md:justify-end">
                            <button
                              type="button"
                              onClick={() => void handleRevokeSession(session.session_id)}
                              disabled={isRevoking}
                              className="inline-flex items-center gap-2 rounded-xl border border-rose-200 bg-white px-2.5 py-1.5 text-xs sm:text-sm font-semibold text-rose-700 transition hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-rose-900/60 dark:bg-slate-900 dark:text-rose-300 dark:hover:bg-rose-950/20"
                            >
                              {isRevoking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                              Sign out
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            <div className="pm-card !p-3 sm:!p-4 !shadow-sm hover:!transform-none hover:!shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <h3 className="text-base sm:text-lg font-bold text-slate-900 dark:text-white">Trusted browsers & desktop apps</h3>
                  <p className="mt-0.5 text-xs sm:text-sm text-slate-600 dark:text-slate-300">
                    These browsers and desktop apps can skip MFA for up to 30 days. Remove trust at any time.
                  </p>
                </div>

                {trustedBrowsers.length > 0 && (
                  <button
                    type="button"
                    onClick={() => void handleRemoveAllTrust()}
                    disabled={bulkRemovingTrust || revokingTrustedBrowserIds.length > 0}
                    className="inline-flex items-center gap-2 rounded-xl bg-rose-600 px-2.5 py-1.5 text-xs sm:text-sm font-semibold text-white shadow-md transition hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {bulkRemovingTrust ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                    Remove all trust
                  </button>
                )}
              </div>

              <div className="mt-2 space-y-2">
                {trustedBrowsers.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50/80 p-4 text-center text-sm text-slate-600 dark:border-slate-700 dark:bg-slate-800/50 dark:text-slate-300">
                    No trusted browsers or desktop apps yet. The next time you complete MFA on a browser or desktop app, you can choose to trust it for 30 days.
                  </div>
                ) : (
                  trustedBrowsers.map((browser) => {
                    const isRevoking = revokingTrustedBrowserIds.includes(browser.app_id);

                    return (
                      <div
                        key={browser.browser_id}
                        className={`rounded-2xl border p-3 transition ${browser.is_current
                          ? 'border-emerald-200 bg-emerald-50/70 dark:border-emerald-900/60 dark:bg-emerald-950/20'
                          : 'border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900/70'
                          }`}
                      >
                        <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                                <Globe className="h-3.5 w-3.5" />
                                {browser.browser_label}
                              </span>
                              {browser.is_current && (
                                <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
                                  <CheckCircle2 className="h-3.5 w-3.5" />
                                  This browser
                                </span>
                              )}
                            </div>
                            <h4 className="mt-1 text-sm sm:text-base font-bold text-slate-900 dark:text-white">{browser.device_name}</h4>
                            <div className="mt-1 grid gap-1 text-xs sm:text-sm text-slate-600 dark:text-slate-300 sm:grid-cols-2">
                              <div>
                                <span className="font-semibold text-slate-700 dark:text-slate-200">Trusted on:</span>{' '}
                                {formatSessionDate(browser.trusted_at)}
                              </div>
                              <div>
                                <span className="font-semibold text-slate-700 dark:text-slate-200">Trust ends:</span>{' '}
                                {formatSessionDate(browser.trusted_until)}
                              </div>
                              <div className="sm:col-span-2">
                                <span className="font-semibold text-slate-700 dark:text-slate-200">Browser code:</span>{' '}
                                <span className="font-mono text-xs break-all">{browser.app_id}</span>
                              </div>
                            </div>
                          </div>

                          <div className="flex flex-wrap gap-2 md:justify-end">
                            <button
                              type="button"
                              onClick={() => void handleRemoveTrustedBrowser(browser.app_id)}
                              disabled={isRevoking}
                              className="inline-flex items-center gap-2 rounded-xl border border-rose-200 bg-white px-2.5 py-1.5 text-xs sm:text-sm font-semibold text-rose-700 transition hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-rose-900/60 dark:bg-slate-900 dark:text-rose-300 dark:hover:bg-rose-950/20"
                            >
                              {isRevoking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                              Remove trust
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </section>

          <aside className="space-y-3">
            <div className="pm-card !p-3 sm:!p-4 !shadow-sm hover:!transform-none hover:!shadow-sm">
              <h3 className="text-base sm:text-lg font-bold text-slate-900 dark:text-white">This device</h3>
              <div className="mt-2 space-y-2 text-xs sm:text-sm text-slate-600 dark:text-slate-300">
                <div className="flex items-start justify-between gap-3 rounded-2xl bg-slate-50 px-3 py-2 dark:bg-slate-800/80">
                  <span className="font-semibold text-slate-700 dark:text-slate-100">Added to this device</span>
                  <span className={`font-semibold ${isStandalonePwaInstalled ? 'text-emerald-600 dark:text-emerald-300' : 'text-slate-500 dark:text-slate-400'}`}>
                    {isStandalonePwaInstalled ? 'Yes' : 'No'}
                  </span>
                </div>
                <div className="flex items-start justify-between gap-3 rounded-2xl bg-slate-50 px-3 py-2 dark:bg-slate-800/80">
                  <span className="font-semibold text-slate-700 dark:text-slate-100">Device code</span>
                  <span className="max-w-[180px] break-all text-right font-mono text-xs text-slate-600 dark:text-slate-300">
                    {userAppId || currentSession?.app_id || 'Not linked'}
                  </span>
                </div>
                <div className="flex items-start justify-between gap-3 rounded-2xl bg-slate-50 px-3 py-2 dark:bg-slate-800/80">
                  <span className="font-semibold text-slate-700 dark:text-slate-100">You&apos;re using TotalAccess in</span>
                  <span className="font-semibold text-slate-700 dark:text-slate-200">
                    {currentSession ? sessionKindMeta[currentSession.session_kind].label : 'Browser'}
                  </span>
                </div>
              </div>
            </div>

            <div className="pm-card !p-3 sm:!p-4 !shadow-sm hover:!transform-none hover:!shadow-sm">
              <h3 className="text-base sm:text-lg font-bold text-slate-900 dark:text-white">Summary</h3>
              <div className="mt-2 grid grid-cols-1 gap-2">
                <div className="rounded-2xl bg-gradient-to-br from-emerald-50 to-cyan-50 p-3 dark:from-emerald-950/20 dark:to-cyan-950/20">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-emerald-700 dark:text-emerald-300">Phones / tablets</p>
                  <p className="mt-1 text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white">{mobileSessions.length}</p>
                </div>
                <div className="rounded-2xl bg-gradient-to-br from-blue-50 to-indigo-50 p-3 dark:from-blue-950/20 dark:to-indigo-950/20">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-blue-700 dark:text-blue-300">Browser sign-ins</p>
                  <p className="mt-1 text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white">{portalSessions.length}</p>
                </div>
                <div className="rounded-2xl bg-gradient-to-br from-slate-100 to-slate-50 p-3 dark:from-slate-800 dark:to-slate-900">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-700 dark:text-slate-300">Desktop sign-ins</p>
                  <p className="mt-1 text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white">{desktopSessions.length}</p>
                </div>
              </div>
            </div>

            <div className="pm-card !p-3 sm:!p-4 !shadow-sm hover:!transform-none hover:!shadow-sm">
              <h3 className="text-base sm:text-lg font-bold text-slate-900 dark:text-white">What we recommend</h3>
              <ol className="mt-2 space-y-2 text-xs sm:text-sm text-slate-600 dark:text-slate-300">
                {showAndroidInstallCard && (
                  <li className="flex gap-2">
                    <span className="mt-0.5 inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-emerald-500 text-[10px] font-bold text-white">1</span>
                    <span>Install the TotalAccess app on this Android device for the smoothest experience.</span>
                  </li>
                )}
                {showAndroidFallbackAction && (
                  <li className="flex gap-2">
                    <span className="mt-0.5 inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-amber-500 text-[10px] font-bold text-white">2</span>
                    <span>If Google Play is not available on this device, continue with the signed-in web app install instead.</span>
                  </li>
                )}
                {showInstalledWebAppCard && (
                  <li className="flex gap-2">
                    <span className="mt-0.5 inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-violet-500 text-[10px] font-bold text-white">1</span>
                    <span>{installedWebAppFlowLabel}</span>
                  </li>
                )}
                {showQrHandoffCard && (
                  <li className="flex gap-2">
                    <span className="mt-0.5 inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-slate-500 text-[10px] font-bold text-white">1</span>
                    <span>Open TotalAccess on your phone to finish setup there.</span>
                  </li>
                )}
                {showContinueOnThisDeviceCard && (
                  <li className="flex gap-2">
                    <span className="mt-0.5 inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-cyan-500 text-[10px] font-bold text-white">1</span>
                    <span>Continue setup on this device because you are already signed in here.</span>
                  </li>
                )}
                <li className="flex gap-2">
                  <span className="mt-0.5 inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-rose-500 text-[10px] font-bold text-white">{showAndroidFallbackAction ? '3' : '2'}</span>
                  <span>After switching devices, sign out the extra phone or tablet here so your account stays linked to the right one.</span>
                </li>
              </ol>
            </div>
          </aside>
        </div>
      </div>
    </>
  );

  if (isPage) {
    if (!portalContainer) {
      return null;
    }

    return createPortal(
      <div
        className="fixed inset-0 isolate overflow-y-auto"
        // Above takeover screens (in-call UI z-[9999], modals up to z-[10050]) but
        // below the global confirm dialog (z-[99997]) and BiometricLockScreen
        // (z-[99999]) so notify.confirm stays usable and the lock screen can gate.
        style={{ zIndex: 99996 }}
      >
        <div className="min-h-screen pm-gradient-hero flex items-center justify-center p-2 sm:p-4">
          <div className="pm-card max-w-6xl w-full max-h-[90vh] overflow-y-auto">
            {pageContent}
          </div>
        </div>
      </div>,
      portalContainer,
    );
  }

  if (!portalContainer) {
    return null;
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[100] bg-gray-50 dark:bg-gray-900 overflow-y-auto"
      data-tour-id="my-devices-modal"
      style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}
      role="dialog"
      aria-modal="true"
      aria-label="My Devices"
    >
      {pageContent}
    </div>,
    portalContainer,
  );
}
